import { useState, useRef, useEffect, useMemo } from "react"
import ConfirmationModal from "../modal/modal-confirmacion-estados"
import { filterStages } from "app/analista/page-analist/infrastructure/dealstages"

interface Option {
    value: string
    label: string
    color: string
}

interface SelectByStatusProps {
    options: Option[]
    value: string | null
    onChange: (value: string) => void
    placeholder?: string
    disabled?: boolean
    dealstage?: string
    hasScoringResult?: boolean
}

const SelectByStatus: React.FC<SelectByStatusProps> = ({ 
    options, 
    value, 
    placeholder = '', 
    disabled = false,
    dealstage,
    hasScoringResult = false
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [pendingSelection, setPendingSelection] = useState<Option | null>(null)
    const selectRef = useRef<HTMLDivElement>(null)

    const selectedOption = options.find((option) => option.value === value) || null
    
    const getOptionStyles = (color: string): string => {
        const colorMap: Record<string, string> = {
            "#E9D8FD": "bg-[#E9D8FD] text-[#44337A]",
            "#BEE3F8": "bg-[#BEE3F8] text-[#2A4365]",
            "#FED7D7": "bg-[#FED7D7] text-[#822727]",
            "#FEEBCB": "bg-[#FEEBCB] text-[#744210]",
            "#C6F6D5": "bg-[#C6F6D5] text-[#22543D]",
            "purple": "bg-[#E9D8FD] text-[#44337A]",
            "blue": "bg-[#BEE3F8] text-[#2A4365]",
            "red": "bg-[#FED7D7] text-[#822727]",
            "orange": "bg-[#FEEBCB] text-[#744210]",
            "green": "bg-[#C6F6D5] text-[#22543D]"
        };
        
        return colorMap[color] || "";
    };
    
    const placeholderOption = useMemo(() => {
        if (!placeholder) return null;

        const stage = filterStages.find(stage => stage.value === placeholder);
        if (stage) {
            return {
                value: stage.value,
                label: stage.label,
                color: stage.color
            };
        }

        const option = options.find(opt => opt.value === placeholder || opt.label === placeholder);
        return option || null;
    }, [placeholder, options]);

    const filteredOptions = useMemo(() => {
        if (dealstage === "24016549") {
            if (hasScoringResult) {
                return [
                    { value: "standby", label: "Standby", color: "orange" },
                    { value: "confirmación de condiciones", label: "Confirmación de condiciones", color: "green" },
                    { value: "negado", label: "Negado", color: "red" }
                ];
            }
            return [{ value: "standby", label: "Standby", color: "orange" }];
        }
        return options;
    }, [dealstage, hasScoringResult, options]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
            setIsOpen(false)
        }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => {
        document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])

    const handleOptionClick = (option: Option) => {
        setPendingSelection(option)
        setShowConfirmModal(true)
        setIsOpen(false)
    }

    const cancelSelection = () => {
        setShowConfirmModal(false)
        setPendingSelection(null)
    }

    const toggleDropdown = () => {
        if (!disabled) {
        setIsOpen(!isOpen)
        }
    }

    return (
        <div className="relative w-full" ref={selectRef}>
            <div className={` relative w-full p-[12px] h-[40px] border border-[#E2E8F0] rounded-[6px] flex items-center justify-between cursor-pointer `} onClick={toggleDropdown}>
                <div className="flex items-center">
                    {selectedOption ? (
                        <span className={`px-2 rounded-full text-sm ${getOptionStyles(selectedOption.color)}`}>
                            {selectedOption.label}
                        </span>
                    ) : (
                        placeholderOption && placeholderOption.color && placeholderOption.label ? (
                            <span className={`px-2 rounded-[2px] font-medium text-xs ${getOptionStyles(placeholderOption.color)}`}>
                                {placeholderOption.label}
                            </span>
                        ) : (
                            <span className="text-xs">{placeholder}</span>
                        )
                    )}
                </div>
                <svg className={`w-5 h-5 text-[#2D3748] transition-transform ${isOpen ? "transform rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>

            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border-[1px] border-gray-300 rounded-[12px] shadow-lg">
                    <div className="py-2 max-h-60 overflow-y-auto">
                        {filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center"
                                onClick={() => handleOptionClick(option)}
                            >
                                <span className={`px-1 py-0.5 rounded-[2px] text-xs font-medium ${getOptionStyles(option.color)}`}>{option.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={cancelSelection}
                selectedOption={pendingSelection}
            />
        </div>
    )
}

export default SelectByStatus
